import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Loading from '../../components/Loading.jsx';
import { ApiError } from '../../lib/api.js';
import { apiPut } from '../../lib/http.js';
import { updateBodySchema } from '../../lib/schemas/eventSettings.js';
import { EVENT_SETTINGS_KEY, useEventSettings } from '../../hooks/useEventSettings.js';

const EMPTY_DEFAULTS = {
  event_date: '',
  event_time: '',
  rsvp_deadline: '',
  location: {
    venue: '',
    city: '',
    country: '',
    maps_url: '',
  },
  ceremony_venue: '',
  ceremony_maps_url: '',
  reception_time: '',
};

// Projects the GET response into the flat/nested shape the form expects.
// Returns EMPTY_DEFAULTS when the query hasn't resolved yet so the form
// keeps rendering predictable, empty inputs.
function settingsToFormValues(data) {
  if (!data) return EMPTY_DEFAULTS;
  return {
    event_date: data.event_date ?? '',
    event_time: data.event_time ?? '',
    rsvp_deadline: data.rsvp_deadline ?? '',
    location: {
      venue: data.location?.venue ?? '',
      city: data.location?.city ?? '',
      country: data.location?.country ?? '',
      maps_url: data.location?.maps_url ?? '',
    },
    ceremony_venue: data.ceremony?.venue ?? '',
    ceremony_maps_url: data.ceremony?.maps_url ?? '',
    reception_time: data.reception_time ?? '',
  };
}

async function saveEventSettings(payload) {
  return apiPut('/api/admin/event-settings', payload);
}

// Admin /evento page (FR-4). Preloads the singleton event-settings row,
// edits the subset covered by `updateBodySchema` (date / time / venue /
// city / country / maps URL) and invalidates the public query on save so
// `/` and `/confirmacion/:token` re-fetch the next time they're visited.
//
// The "Última edición por..." banner only renders when the response
// payload carries both `updated_at` and `updated_by`. The current public
// GET omits them on purpose, so the banner is rendered defensively and
// lights up as soon as the admin endpoint is wired (or whenever the row
// is returned with those fields).
export default function AdminEvento() {
  const { data, isLoading, error } = useEventSettings();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(updateBodySchema),
    defaultValues: EMPTY_DEFAULTS,
    mode: 'onTouched',
  });

  // Seed form values once the GET response arrives, but only when the
  // user hasn't started editing. Otherwise we'd wipe their in-progress
  // changes every time the query re-fetches.
  useEffect(() => {
    if (data && !isDirty) {
      reset(settingsToFormValues(data));
    }
  }, [data, isDirty, reset]);

  const saveMutation = useMutation({
    mutationFn: saveEventSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EVENT_SETTINGS_KEY });
      toast.success('Cambios guardados.');
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : 'No pudimos guardar los cambios. Intentá de nuevo.';
      toast.error(message);
    },
  });

  async function onSubmit(values) {
    try {
      await saveMutation.mutateAsync({
        event_date: values.event_date,
        event_time: values.event_time,
        rsvp_deadline: values.rsvp_deadline,
        location: {
          venue: values.location.venue.trim(),
          city: values.location.city.trim(),
          country: values.location.country.trim(),
          maps_url: values.location.maps_url ?? '',
        },
        ceremony_venue: values.ceremony_venue.trim(),
        ceremony_maps_url: values.ceremony_maps_url ?? '',
        reception_time: values.reception_time,
      });
    } catch {
      // useMutation.onError already surfaces the toast for the user; we
      // swallow here so react-hook-form's handleSubmit doesn't surface
      // the rejected promise (would otherwise turn into a console.error
      // in dev and an unhandled rejection in tests).
    }
  }

  if (isLoading && !data) {
    return <Loading label="Cargando configuración del evento..." />;
  }

  const showUpdatedBanner = Boolean(data?.updated_at && data?.updated_by);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl text-text">Evento</h1>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-accent hover:underline"
        >
          Ver invitación
        </a>
      </header>

      {showUpdatedBanner && (
        <Card tone="success" className="text-sm" data-testid="updated-banner">
          Última edición por <strong>{data.updated_by}</strong> el {data.updated_at}.
        </Card>
      )}

      {error && (
        <Card tone="error" className="text-sm" role="alert">
          No pudimos cargar la configuración del evento. Refrescá la página para reintentar.
        </Card>
      )}

      <Card>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          aria-label="Formulario de evento"
        >
          <Input
            label="Fecha del evento"
            type="date"
            {...register('event_date')}
            error={errors.event_date?.message}
          />
          <Input
            label="Hora"
            type="time"
            {...register('event_time')}
            error={errors.event_time?.message}
          />
          <Input
            label="Fecha límite para confirmar"
            type="date"
            {...register('rsvp_deadline')}
            error={errors.rsvp_deadline?.message}
          />
          <p className="-mt-2 text-xs text-text-muted">
            Hasta el final de ese día.
          </p>
          <Input
            label="Lugar / Venue"
            {...register('location.venue')}
            error={errors.location?.venue?.message}
          />
          <Input
            label="Ciudad"
            {...register('location.city')}
            error={errors.location?.city?.message}
          />
          <Input
            label="País"
            {...register('location.country')}
            error={errors.location?.country?.message}
          />
          <Input
            label="URL de Google Maps (opcional)"
            type="url"
            placeholder="https://maps.app.goo.gl/..."
            autoComplete="off"
            {...register('location.maps_url')}
            error={errors.location?.maps_url?.message}
          />
          <p className="-mt-2 text-xs text-text-muted">
            Pegá el link de Google Maps (Compartir &rarr; Copiar link).
          </p>

          <hr className="my-2 border-border" />

          <h2 className="font-heading text-lg text-text">Ceremonia</h2>
          <Input
            label="Lugar de la ceremonia"
            {...register('ceremony_venue')}
            error={errors.ceremony_venue?.message}
          />
          <Input
            label="URL de Google Maps (opcional)"
            type="url"
            placeholder="https://maps.app.goo.gl/..."
            autoComplete="off"
            {...register('ceremony_maps_url')}
            error={errors.ceremony_maps_url?.message}
          />
          <p className="-mt-2 text-xs text-text-muted">
            Pegá el link de Google Maps (Compartir &rarr; Copiar link).
          </p>

          <hr className="my-2 border-border" />

          <h2 className="font-heading text-lg text-text">Recepci&oacute;n</h2>
          <Input
            label="Hora de la recepci&oacute;n"
            type="time"
            {...register('reception_time')}
            error={errors.reception_time?.message}
          />

          <div className="mt-2 flex justify-end gap-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
